<template>
  <BgImageLayout title="Financial Periods Settings" :page="__guide.financialSettings" icon-color="white">
    <div class="col-xs-12">
      <AgGridWrapper
        ref="agGrid"
        :options="gridOptions"
        handle-update-on-parent
        @cell-updated="cellUpdated"
        @selectedRows="selectSubscriptions"
      >
        <template #header-row>
          <Grid gaf="column" justify-content="space-between" class="header-row">
            <YearDropdown :year="year" />

            <Grid gaf="column" gap="10px" align-items="center" class="right">
              <DropdownBtn
                v-if="selectedSubscriptionsIds.length"
                ref="dropdownAction"
                :disable-btn="!bulkUpdateValue"
                :loading-btn="bulkUpdateLoading"
                label="Edit selected"
                @update="bulkUpdate"
              >
                <q-select
                  v-model="bulkUpdateValue"
                  label="Select Financial period"
                  :options="financialPeriodOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                />
              </DropdownBtn>
            </Grid>
          </Grid>
        </template>
      </AgGridWrapper>
    </div>
  </BgImageLayout>
</template>

<script>
import * as apiTo from '@/modular-api'
import gridOptions from './gridOptions'
// Libraries
import { cloneDeep } from 'lodash'
// Components
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import DropdownBtn from './components/DropdownBtn'
import YearDropdown from './components/YearDropdown'

export default {
  name: 'FinancialPeriods',

  components: {
    AgGridWrapper,
    DropdownBtn,
    YearDropdown
  },

  mixins: [gridOptions],

  props: {
    year: String // from route
  },

  data: () => ({
    subscriptions: null,
    clonedSubscriptions: null,
    selectedSubscriptionsIds: [],
    bulkUpdateValue: null,
    bulkUpdateLoading: false,
    financialPeriods: []
  }),

  computed: {
    financialPeriodOptions() {
      return this.financialPeriods.map(period => ({
        label: `${this.formatDateForLocale(period.start_date)} - ${this.formatDateForLocale(period.end_date)}`,
        value: period
      }))
    }
  },

  watch: {
    year: {
      handler: 'getSubscriptions',
      immediate: true
    }
  },

  created() {
    this.getAndSetFinancialPeriods()
  },

  methods: {
    getAndSetFinancialPeriods() {
      apiTo.GET_FINANCIAL_PERIODS().then(({ data }) => (this.financialPeriods = data.result))
    },

    selectSubscriptions(subsIds) {
      if (!subsIds.length) this.bulkUpdateValue = null // reset bulkUpdateValue when clearing selection
      this.selectedSubscriptionsIds = subsIds
    },

    getSubscriptions(year) {
      apiTo.GET_SUBSCRIPTIONS_PERIODS_WITH_FILTERS(year).then(({ data }) => {
        this.selectedSubscriptionsIds = []
        this.subscriptions = data.result
        this.clonedSubscriptions = cloneDeep(data.result)
      })
    },

    cellUpdated(payload) {
      const itemToUpdate = [{ id: payload.data.id, financial_period_id: payload.newValue.id }]

      this.updateChanges(itemToUpdate)
    },

    updateChanges(items) {
      return apiTo.MODIFY_SUBSCRIPTION_PERIODS({ items }).then(() => {
        this.clonedSubscriptions = cloneDeep(this.subscriptions)
        this.$refs.agGrid.updateRows(this.subscriptions)
      })
    },

    async bulkUpdate() {
      const itemsToUpdate = this.getItemsToBulkUpdate()

      this.bulkUpdateLoading = true
      await this.updateChanges(itemsToUpdate)
      this.bulkUpdateLoading = false
      this.$refs.dropdownAction.hide()
      this.bulkUpdateValue = null
      this.selectedSubscriptionsIds = []
      this.$refs.agGrid.deselectAllRows()
    },

    getItemsToBulkUpdate() {
      return this.selectedSubscriptionsIds.reduce((acc, subId) => {
        const index = this.subscriptions.findIndex(s => s.id === subId)
        if (~index) {
          this.subscriptions[index].financial_period = this.bulkUpdateValue
          acc.push({
            id: this.subscriptions[index].id,
            financial_period_id: this.bulkUpdateValue.id
          })
        }
        return acc
      }, [])
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/mixins'

>>> .ag-overlay-wrapper {
  background rgba(white, 0.3)
}


.header-row {
  padding 15px
}

tableWithCheckBoxes()

>>> .table-content {
  height calc(100vh - 180px) !important
}

>>> .ag-theme-material {
  height calc(100%)
}

>>> .ag-floating-top {
  overflow-y auto !important
}

styledScrollFor('>>> .ag-body-viewport.ag-layout-normal');
</style>
